import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";

interface CampaignInfo {
  nome: string;
  attiva: boolean;
  richiedeApprovazione: boolean;
}

export default function Register() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [campaign, setCampaign] = useState<CampaignInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Form fields
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [consenso, setConsenso] = useState(false);
  // Honeypot: campo che gli umani NON compileranno.
  // Stile inline tutto a -9999px per essere SICURI che nessuno screen reader
  // lo legga come campo da compilare (alcuni leggono "display: none" male).
  const [azienda, setAzienda] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<{ richiedeApprovazione: boolean } | null>(null);

  useEffect(() => {
    api.get<CampaignInfo>(`/register/${slug}`)
      .then(setCampaign)
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (nome.trim().length < 1) e.nome = "Inserisci il tuo nome";
    if (cognome.trim().length < 1) e.cognome = "Inserisci il tuo cognome";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = "Email non valida";
    if (!consenso) e.consenso = "Consenso obbligatorio per procedere";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true); setServerError(null);
    try {
      const r = await api.post<{ ok: true; richiedeApprovazione: boolean }>(
        `/register/${slug}`,
        {
          nome: nome.trim(),
          cognome: cognome.trim(),
          email: email.trim().toLowerCase(),
          telefono: telefono.trim() || undefined,
          consenso,
          azienda, // honeypot
        },
      );
      setSuccess({ richiedeApprovazione: r.richiedeApprovazione });
    } catch (e: any) {
      setServerError(e.message);
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  }

  if (loadError || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md text-center space-y-3">
          <div className="text-5xl">🚫</div>
          <h1 className="text-xl font-serif text-primary">Registrazione non disponibile</h1>
          <p className="text-secondary">{loadError ?? "Campagna non trovata"}</p>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md text-center space-y-3">
          <div className="text-6xl">✅</div>
          <h1 className="text-2xl font-serif text-primary">Registrazione ricevuta</h1>
          {success.richiedeApprovazione ? (
            <p className="text-secondary">
              I tuoi dati sono in revisione. Il docente li approverà a breve e ti contatterà
              al tuo indirizzo email.
            </p>
          ) : (
            <p className="text-secondary">
              Sei registrato! Riceverai presto le credenziali sulla tua email.
            </p>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <Card className="max-w-lg w-full space-y-5">
        <header className="space-y-1">
          <div className="font-mono text-xs uppercase tracking-wider text-secondary">
            Registrazione
          </div>
          <h1 className="text-3xl font-serif text-primary">{campaign.nome}</h1>
        </header>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
              error={errors.nome} required autoFocus />
            <Input label="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)}
              error={errors.cognome} required />
          </div>
          <Input label="Email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} error={errors.email}
            placeholder="tu@esempio.it" required />
          <Input label="Telefono (opzionale, per WhatsApp)"
            value={telefono} onChange={(e) => setTelefono(e.target.value)}
            placeholder="+39 333 123 4567" />

          {/* Honeypot — invisibile a umani, ASCII-only per evitare side effects. */}
          <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
          >
            <label>
              Azienda (lascia vuoto)
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={azienda}
                onChange={(e) => setAzienda(e.target.value)}
              />
            </label>
          </div>

          <label className="flex items-start gap-2 text-sm text-secondary cursor-pointer">
            <input type="checkbox" checked={consenso}
              onChange={(e) => setConsenso(e.target.checked)} className="mt-1" />
            <span>
              Acconsento al trattamento dei miei dati per essere ricontattato in relazione
              al percorso formativo ITS Marketing.
              {errors.consenso && (
                <span className="block text-red-400 text-xs mt-1">{errors.consenso}</span>
              )}
            </span>
          </label>

          {serverError && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-sm text-red-300">
              {serverError}
            </div>
          )}

          <Button type="submit" loading={sending} className="w-full">
            Registrami →
          </Button>
        </form>
      </Card>
    </div>
  );
}
